import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

// Diagnostics for report pages that "spin for a long time" and cannot be reproduced with the network tab open
// (26/09/2026). For every request to the report API it logs to the console when the request is still pending after
// SLOW_MS, and again when it finishes (status and duration, or "cancelled" if the page dropped it). The same entries are
// kept in sessionStorage ("report_slow_requests", the last 30) so they can be read after the fact even when the console
// was closed:  JSON.parse(sessionStorage.getItem('report_slow_requests'))
export const SLOW_REPORT_REQUEST_STORAGE_KEY = 'report_slow_requests';
const SLOW_MS = 8000;
const MAX_ENTRIES = 30;

@Injectable()
export class SlowReportRequestInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.includes('/report/')) {
      return next.handle(req);
    }

    const label = `${req.method} ${req.urlWithParams.replace(/^https?:\/\/[^/]+/, '')}`;
    const startedAt = performance.now();
    let outcome = 'cancelled';
    const pendingTimer = setTimeout(() => {
      console.warn(`[report] still pending after ${SLOW_MS / 1000}s: ${label}`);
      this.record({ url: label, ms: SLOW_MS, outcome: 'pending' });
    }, SLOW_MS);

    return next.handle(req).pipe(
      tap({
        next: event => { if (event instanceof HttpResponse) outcome = String(event.status); },
        error: err => { outcome = `error ${err?.status ?? ''}`.trim(); }
      }),
      finalize(() => {
        clearTimeout(pendingTimer);
        const ms = Math.round(performance.now() - startedAt);
        if (ms >= SLOW_MS) {
          console.warn(`[report] finished after ${ms} ms (${outcome}): ${label}`);
          this.record({ url: label, ms, outcome });
        }
      })
    );
  }

  private record(entry: { url: string; ms: number; outcome: string }) {
    try {
      const list = JSON.parse(sessionStorage.getItem(SLOW_REPORT_REQUEST_STORAGE_KEY) ?? '[]') as unknown[];
      list.push({ at: new Date().toISOString(), ...entry });
      sessionStorage.setItem(SLOW_REPORT_REQUEST_STORAGE_KEY, JSON.stringify(list.slice(-MAX_ENTRIES)));
    } catch {
      // storage unavailable - the console line above is enough
    }
  }
}
